<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import Tooltip from '@/components/common/base/Tooltip';
import AcceptIcon from '@/components/common/base/AcceptIcon.vue';
import '@sapudex/web-components/dist/ProgressBar.js';
import '@sapudex/web-components/dist/Link.js';
import { openComponent } from '../../plugins/profile/progressCompenentPlugin';

defineOptions({
  name: 'PEProfileProgressComponent',
});

const PROFILE_INITIAL_PROGRESS = [
  {
    name: ['logoUrl'],
    complete: false,
    behavior: 'EMIT',
    info: 'STEP1_LOGO',
  },
  {
    name: ['videoImageUrl'],
    complete: false,
    behavior: 'SCROLL',
    info: 'pe-video-upload-container',
  },
  {
    name: ['resources'],
    complete: false,
    behavior: 'SCROLL',
    info: 'global-resource-section',
  },
];

const INIT_PROGRESS = 60;

const store = useStore();

const profileProgress = ref([...PROFILE_INITIAL_PROGRESS]);
const currentStep = ref(0);
const progressCompleted = ref('INITIAL_STATE');
const progressPercentage = ref(INIT_PROGRESS);
const showCompletedProgressBar = ref('INITIAL_STATE');
const openCompleteModalOnce = ref(false);

const profileData = computed(() => store.state.partnerProfile.profileData);
const errorFields = computed(() => store.state.partnerProfile.errorFields);
const publishDate = computed(() => store.state.partnerProfile.profileData.publishDate);
const contactFormsCount = computed(() => store.state.partnerProfile.profileData.contactFormsCount);

const profilePublished = computed(() => {
  return !!publishDate.value;
});

function checkEmptyField(field) {
  return (
    (Array.isArray(field) && field.length > 0) || (typeof field === 'string' && field.length > 0)
  );
}

function updateCurrentStep() {
  currentStep.value = profileProgress.value.findIndex((step) => step.complete === false);
}

function updateProgressPercentage() {
  progressPercentage.value = profileProgress.value.reduce(
    (accumulator, currentValue) => accumulator + (currentValue.complete ? 10 : 0),
    INIT_PROGRESS
  );
  store.commit('partnerProfile/changeProfileProgressPercentage', progressPercentage.value);
}

function updateProgressCompleted() {
  const totalStep = profileProgress.value.length - 1;
  if (
    (currentStep.value === totalStep && profileProgress.value[totalStep].complete) ||
    currentStep.value === -1
  ) {
    progressCompleted.value = true;
  } else {
    progressCompleted.value = false;
  }
}

function updateProfileProgress() {
  profileProgress.value.forEach((step) => {
    const initialValue = false;
    step.complete = step.name.reduce(
      (accumulator, currentValue) =>
        accumulator || checkEmptyField(profileData.value[currentValue]),
      initialValue
    );
  });
  updateCurrentStep();
  updateProgressPercentage();
  updateProgressCompleted();
}

function toggleModal(val) {
  if (progressCompleted.value) {
    store.commit('partnerProfile/changeShowCongratulationsModal', val);
    return;
  }
  store.commit('partnerProfile/changeProfileProgressData', profileProgress.value);
  store.commit('partnerProfile/changeProfileProgressPercentage', progressPercentage.value);
  store.commit('partnerProfile/changeShowProgressModal', val);
}

watch(
  profileData,
  () => {
    updateProfileProgress();
  },
  { deep: true }
);

watch(progressCompleted, (newVal, oldVal) => {
  if (oldVal !== 'INITIAL_STATE') {
    showCompletedProgressBar.value = newVal;
    if (!openCompleteModalOnce.value && newVal === true) {
      setTimeout(() => {
        toggleModal(true);
        openCompleteModalOnce.value = true;
      }, 500);
    }
  }
});
</script>

<template>
  <div
    class="progress-bar-incomplete flex flex-col"
    v-if="profilePublished && !progressCompleted && errorFields && errorFields.length === 0"
  >
    <div class="progress-bar flex justify-start gap-3">
      <udex-button
        class="mt-2"
        size="Medium"
        design="Invisible"
        @click="toggleModal(true)"
        accessibleName="View profile progress details"
        >{{ $t('progress.bar.label') }}</udex-button
      >
      <udex-progress-bar
        class="w-48"
        :value="progressPercentage"
        :accessibleName="`Profile completion progress: ${progressPercentage}%`"
      />
    </div>
    <udex-link
      icon="slim-arrow-right"
      icon-placement="End"
      @click="openComponent(profileProgress[currentStep])"
      :accessibleName="`Go to ${$t(`progress-bar-next-step-text[${currentStep}]`)} section`"
    >
      {{ $t('progress.bar.next.step') }}
      {{ $t(`progress-bar-next-step-text[${currentStep}]`) }}
    </udex-link>
  </div>
  <div
    v-else-if="
      profilePublished &&
      showCompletedProgressBar === true &&
      progressCompleted === true &&
      errorFields &&
      errorFields.length === 0
    "
    class="progress-bar-completed"
  >
    <div class="progress-improved-container flex flex-col gap-1">
      <div class="HeadingMediumXXXXS-size flex gap-1">
        {{ $t('profile.complete.title') }}
        <AcceptIcon width="16" height="16" variant="blue" />
      </div>
      <div class="HeadingRegularXXXXS-size">
        {{ $t('profile.complete.text') }}
      </div>
    </div>
  </div>
  <div
    v-else-if="
      profilePublished &&
      showCompletedProgressBar === 'INITIAL_STATE' &&
      progressCompleted === true &&
      contactFormsCount &&
      contactFormsCount > 0
    "
    class="progress-bar-contact-num flex"
  >
    <div class="profile-complete-analytic flex items-center">
      <div class="contact-num HeadingMediumXXXXS-size mr-1">{{ contactFormsCount }}</div>
      <div class="contact-text HeadingMediumXXXXS-size !font-normal !text-grey-9">
        {{ $t('progress.bar.complete.contact') }}
      </div>
      <Tooltip>
        <template #text>{{ $t('progress.bar.complete.help') }}</template>
      </Tooltip>
    </div>
  </div>
</template>

<style scoped lang="less">
.progress-bar > udex-button {
  --udex-button-padding-medium: 0px 0px;
  --udex-button-color-invisible-standard-default-text-and-icon: var(--color-blue-7);

}
</style>
